import * as THREE from "three";
import { BASE_RES, allCells, cellRes, cellScale, cellToLatLng, circleRadiusRad, netCellsWithin, netSpacingRad } from "./fib-grid";

// Project lat/lng (degrees) to a unit-sphere direction matching the rest of
// the codebase's convention: (lat=0, lng=0) → +Z.
function polar2CartesianUnit(latDeg, lngDeg) {
  const phi = ((90 - latDeg) * Math.PI) / 180;
  const theta = ((90 - lngDeg) * Math.PI) / 180;
  return new THREE.Vector3(
    Math.sin(phi) * Math.cos(theta),
    Math.cos(phi),
    Math.sin(phi) * Math.sin(theta)
  );
}

// Depth of the "button" prism, in globe-radius units. Small enough to keep
// the buttons low-profile but big enough to read as 3D from orbit view.
const BUTTON_DEPTH = 0.004;
// How far the middle of a circle's domed top rises above its edge, in
// globe-radius units at the base grid level (scaled by each level's cellScale).
const DOME_HEIGHT = 0.004;
// Rings of the dome, as fractions of the circle's radius (the middle is a fan).
// The profile is a spherical cap: height = sqrt(1 - f²) of the dome height.
const DOME_RINGS = [0.55, 0.85];
// Size of a circle's glow sprite, in the dots' point-size units at the base level.
const HALO_POINT_SIZE = 28;
// Strength of a story's resting glow sprite (its dot carries the story now).
const HALO_REST_GLOW = "0.2";
// How far a story's halo sprite floats above the net (globe radii at the
// base level, scaled by cellScale).
export const HALO_LIFT = 2 * DOME_HEIGHT;
// The selection ring: a crisp stroke of constant screen width around the
// story's dot. Its radius is RING_RADII × the story's circle radius, kept
// between RING_MIN_PX and RING_MAX_PX (CSS pixels); it settles in from
// RING_SETTLE_FROM × over RING_SETTLE_S seconds. The callout anchors to it.
export const RING_RADII = 1.6;
export const RING_MIN_PX = 10;
export const RING_MAX_PX = 22;
export const RING_WIDTH_PX = 2;
const RING_SETTLE_FROM = "1.35";
const RING_SETTLE_S = "0.3";
// How much of the category colour the white ring takes.
const RING_TINT = "0.15";
// A story is drawn as the net dot at its centre, grown: its size runs from
// the first (least important) to the second times a full net dot's. It is the
// net's colour, turning white while hovered.
const STORY_DOT_SIZE = ["2.0", "3.2"];
// Net dots within this of a story's centre are its dot (radians at the base
// level, scaled by cellScale): half the net's spacing, so exactly one.
const STORY_DOT_RAD = 0.5 * netSpacingRad(BASE_RES);
// Where the net fades out around a story, half visible (radians at the base
// grid level, scaled by each level's cellScale). Wider than the height smoothing's reach,
// so the net fades out over a broad area rather than at a rim.
const NET_REACH_RAD = 0.07;
// The weakest story's net reach, as a fraction of the strongest's.
const NET_MIN_REACH = "0.4";
// The weakest story's net strength, as a fraction of the strongest's.
const NET_MIN_STRENGTH = "0.5";
// Size of a dot where the net has faded out, as a fraction of its full size.
const NET_MIN_DOT_SIZE = "0.18";
// Most stories the net's visibility loop looks at (slots beyond are ignored).
const MAX_NET_STORIES = 2048;
// Height of the net (and the discs on it) under a story of full importance
// weight, in globe radii at the base grid level (scaled by cellScale).
const NET_HEAT_HEIGHT = 0.12;
// New stories' pulses: a ring of light every PULSE_PERIOD seconds that takes
// PULSE_TRAVEL seconds to run out to PULSE_OVERSHOOT × the story's zone edge.
const PULSE_PERIOD = "4.0";
const PULSE_TRAVEL = "2.5";
const PULSE_OVERSHOOT = "1.3";
// How sharply a big story's hill peaks: height ∝ (1 - distance/reach)^this.
// Above 1 gives a pointed, volcano-like summit with concave flanks; higher is
// needle-like.
const NET_PEAK_SHARPNESS = "2.5";
// Small stories are soft mounds instead (smoothstep: rounded top, easing
// into the ground), blending into the volcano between these weights.
const NET_MOUND_BELOW = "0.25";
const NET_VOLCANO_ABOVE = "0.75";
// Hill profile at t (1 at the summit, 0 at the foot) for a story's weight.
function netHillShape(t, weight) {
  const lo = parseFloat(NET_MOUND_BELOW), hi = parseFloat(NET_VOLCANO_ABOVE);
  const x = Math.min(1, Math.max(0, (weight - lo) / (hi - lo)));
  const sharp = x * x * (3 - 2 * x);
  const mound = t * t * (3 - 2 * t);
  return mound + (Math.pow(t, parseFloat(NET_PEAK_SHARPNESS)) - mound) * sharp;
}
// How much more height big stories get than small ones: a hill's height is
// weight^this (weights run 0.1..1). At 2.5 a median story (weight ~0.3) rises
// to ~5% of the tallest; at 1 it would be ~30%.
const NET_HEIGHT_CONTRAST = "2.5";

// The net's height, as the dot shader's netVisibility computes it (keep the
// two in sync): 0..1 of NET_HEAT_HEIGHT at unit direction (x, y, z) — the
// tallest importance-weighted hill there — from the story texels in `stories`
// (centre × (1 + weight), presence) and the level's net reach.
function netHeightAt(stories, count, x, y, z, reach) {
  const edge = reach * 2.5;
  const minReach = parseFloat(NET_MIN_REACH);
  let height = 0;
  for (let s = 0; s < count; s++) {
    const j = s * 4;
    const pres = stories[j + 3];
    if (pres <= 0) continue;
    const len = Math.hypot(stories[j], stories[j + 1], stories[j + 2]);
    const weight = len - 1;
    const c = (x * stories[j] + y * stories[j + 1] + z * stories[j + 2]) / len;
    const storyEdge = edge * (minReach + (1 - minReach) * weight) * pres;
    const d = Math.acos(Math.min(1, Math.max(-1, c)));
    if (d >= storyEdge) continue;
    const t = 1 - d / storyEdge;
    height = Math.max(height, Math.pow(weight, parseFloat(NET_HEIGHT_CONTRAST)) * pres * netHillShape(t, weight));
  }
  return height;
}

// Builds the news grid: one dot per grid point (fib-grid.js), plus the pooled
// geometry for filled cells and their halos.
//
// Returns:
//   dotGeometry     — THREE.BufferGeometry suitable for THREE.Points
//   applyAltitudes  — (altitude per cell index, presence by cell id) => void
//   setSurfaceCells / setHiddenCells — the filled cells and the dots they hide
export function buildHexWireframeGeometry({
  resolution,
  cells: onlyCells = null, // a region's cells; default every point at `resolution`
  radius,
} = {}) {
  const cells = onlyCells || allCells(resolution);
  const cellIndex = new Map(cells.map((c, i) => [c, i]));
  const numCells = cells.length;
  // The net's heatmap reach and height at this level (the dot shader gets the
  // same values through createHexWireframeMaterial).
  const netReach = NET_REACH_RAD * cellScale(resolution);
  const heatHeight = NET_HEAT_HEIGHT * cellScale(resolution);

  const dotPositions = new Float32Array(numCells * 3);
  const dotDirs = new Float32Array(numCells * 3);
  for (let i = 0; i < numCells; i++) {
    const [lat, lng] = cellToLatLng(cells[i]);
    const d = polar2CartesianUnit(lat, lng);
    dotDirs[i * 3] = d.x;
    dotDirs[i * 3 + 1] = d.y;
    dotDirs[i * 3 + 2] = d.z;
    dotPositions[i * 3] = d.x * radius;
    dotPositions[i * 3 + 1] = d.y * radius;
    dotPositions[i * 3 + 2] = d.z * radius;
  }
  const dotPositionAttr = new THREE.Float32BufferAttribute(dotPositions, 3);
  dotPositionAttr.setUsage(THREE.DynamicDrawUsage);

  const dotAltitudeAttr = new THREE.Float32BufferAttribute(new Float32Array(numCells), 1);
  dotAltitudeAttr.setUsage(THREE.DynamicDrawUsage);

  const dotHiddenAttr = new THREE.Float32BufferAttribute(new Float32Array(numCells), 1);
  dotHiddenAttr.setUsage(THREE.DynamicDrawUsage);

  const dotGeometry = new THREE.BufferGeometry();
  dotGeometry.setAttribute("position", dotPositionAttr);
  dotGeometry.setAttribute("aAltitude", dotAltitudeAttr);
  dotGeometry.setAttribute("aHidden", dotHiddenAttr);

  // Hide the dots that lie under filled cells: every net dot within a
  // circle's radius. Incremental: only dots whose cover changed are touched.
  const coveredCache = new Map();
  const covered = (id) => {
    let dots = coveredCache.get(id);
    if (!dots) {
      if (coveredCache.size > 4000) coveredCache.clear();
      dots = [];
      for (const d of netCellsWithin(id, circleRadiusRad(cellRes(id)))) {
        const ci = cellIndex.get(d);
        if (ci !== undefined) dots.push(ci);
      }
      coveredCache.set(id, dots);
    }
    return dots;
  };
  let hiddenDots = new Set();
  const setHiddenCells = (filledCellIds) => {
    const filled = filledCellIds instanceof Set ? filledCellIds : new Set(filledCellIds || []);
    const arr = dotHiddenAttr.array;
    const next = new Set();
    for (const id of filled) for (const ci of covered(id)) next.add(ci);
    let changed = false;
    for (const ci of hiddenDots) {
      if (next.has(ci)) continue;
      arr[ci] = 0;
      changed = true;
    }
    for (const ci of next) {
      if (hiddenDots.has(ci)) continue;
      arr[ci] = 1;
      changed = true;
    }
    hiddenDots = next;
    if (changed) dotHiddenAttr.needsUpdate = true;
  };

  // Filled hexes ("buttons") and their halos. Vertex positions are static
  // unit directions; each hex's top altitude and grow/shrink amount live in a
  // small float texture (one texel per hex "slot") read by the vertex
  // shaders, so per-frame animation touches a few hundred values instead of
  // ~1000 vertices per hex.
  let surfaceGeometry = null;
  let haloGeometry = null;
  let slotTexture = null;
  let slotData = null;             // RGBA per slot: (top altitude, presence, -, -)
  let storyTexture = null;
  let storyData = null;            // RGBA per slot: (centre × (1 + weight), presence), for the net's visibility
  let pulseTexture = null;
  let pulseData = null;            // RGBA per slot: (newness 0..1, -, -, -), for new stories' pulses
  let surfaceCellIds = [];         // slot → cellId (null when free)
  const SLOT_TEX_WIDTH = 256;


  // Per-cell vertex templates (unit directions etc.), built once per cell and
  // reused, so rebuilding the surface while scrubbing is just array copies.
  const MAX_CELL_TEMPLATES = 2000;
  const CIRCLE_SEGMENTS = 32;
  const PERIMETER_MAX = CIRCLE_SEGMENTS;
  // Per segment: a dome fan (1) + a quad per further ring (2 each) on each of the top and
  // bottom, plus the wall (2) triangles.
  const SURFACE_VERTS_PER_SLOT = PERIMETER_MAX * 3 * (2 * (1 + 2 * DOME_RINGS.length) + 2);
  const HALO_VERTS_PER_SLOT = 1;                     // one camera-aligned glow sprite
  const cellTemplates = new Map();
  const cellTemplate = (cellId) => {
    let tpl = cellTemplates.get(cellId);
    if (tpl) return tpl;

    const [cLat, cLng] = cellToLatLng(cellId);
    const c = polar2CartesianUnit(cLat, cLng);

    // Filled cells are circles of one fixed angular radius per grid level
    // (fib-grid.js), so they look the same everywhere on the globe.
    const radiusRad = circleRadiusRad(cellRes(cellId));
    // Tangent basis at the centre (any orientation will do for a circle).
    const ref = Math.abs(c.y) < 0.99 ? { x: 0, y: 1, z: 0 } : { x: 1, y: 0, z: 0 };
    let e1x = ref.y * c.z - ref.z * c.y, e1y = ref.z * c.x - ref.x * c.z, e1z = ref.x * c.y - ref.y * c.x;
    const e1n = 1 / Math.hypot(e1x, e1y, e1z);
    e1x *= e1n; e1y *= e1n; e1z *= e1n;
    const e2x = c.y * e1z - c.z * e1y, e2y = c.z * e1x - c.x * e1z, e2z = c.x * e1y - c.y * e1x;
    const sinR = Math.sin(radiusRad), cosR = Math.cos(radiusRad);
    // The ring of points at `frac` of the circle's radius.
    const ringDirs = (frac) => {
      const sinF = Math.sin(radiusRad * frac), cosF = Math.cos(radiusRad * frac);
      const ring = [];
      for (let i = 0; i < CIRCLE_SEGMENTS; i++) {
        const a = (i / CIRCLE_SEGMENTS) * Math.PI * 2;
        const ca = Math.cos(a) * sinF, sa = Math.sin(a) * sinF;
        ring.push({
          x: c.x * cosF + e1x * ca + e2x * sa,
          y: c.y * cosF + e1y * ca + e2y * sa,
          z: c.z * cosF + e1z * ca + e2z * sa,
        });
      }
      return ring;
    };
    const roundedDirs = ringDirs(1);
    const domeRings = DOME_RINGS.map(ringDirs);
    // aIsTop carries the domes: 1 + height (0..1 of the dome height) on top
    // (the middle is 2, the edge 1), and minus the depth (0 at the edge, -1 in
    // the middle) on the underside.
    const domeValue = (frac) => 1 + Math.sqrt(1 - frac * frac);
    const bowlValue = (frac) => -Math.sqrt(1 - frac * frac);
    const m = roundedDirs.length;

    const sDirs = [];
    const sIsTop = [];
    const hDirs = [];
    const hGlow = [];
    const push = (arr, ...vs) => { for (const v of vs) arr.push(v.x, v.y, v.z); };
    for (let i = 0; i < m; i++) {
      const dir1 = roundedDirs[i];
      const dir2 = roundedDirs[(i + 1) % m];
      // Domed top: a fan from the middle to the first ring, then a band of
      // two triangles out to each following ring and the edge.
      const rings = [...domeRings.map((r, k) => ({ r, v: domeValue(DOME_RINGS[k]) })), { r: roundedDirs, v: 1 }];
      push(sDirs, c, rings[0].r[i], rings[0].r[(i + 1) % m]);
      sIsTop.push(2, rings[0].v, rings[0].v);
      for (let k = 0; k + 1 < rings.length; k++) {
        const inA = rings[k].r[i], inB = rings[k].r[(i + 1) % m];
        const outA = rings[k + 1].r[i], outB = rings[k + 1].r[(i + 1) % m];
        push(sDirs, inA, outA, outB, inA, outB, inB);
        sIsTop.push(rings[k].v, rings[k + 1].v, rings[k + 1].v, rings[k].v, rings[k + 1].v, rings[k].v);
      }
      // Side wall — two triangles (T1,B1,T2) + (B1,B2,T2). Outward-facing winding.
      push(sDirs, dir1, dir1, dir2, dir1, dir2, dir2);
      sIsTop.push(1, 0, 1, 0, 0, 1);
      // Domed underside: the same rings mirrored, wound the other way.
      const bowl = [...domeRings.map((r, k) => ({ r, v: bowlValue(DOME_RINGS[k]) })), { r: roundedDirs, v: 0 }];
      push(sDirs, c, bowl[0].r[(i + 1) % m], bowl[0].r[i]);
      sIsTop.push(-1, bowl[0].v, bowl[0].v);
      for (let k = 0; k + 1 < bowl.length; k++) {
        const inA = bowl[k].r[i], inB = bowl[k].r[(i + 1) % m];
        const outA = bowl[k + 1].r[i], outB = bowl[k + 1].r[(i + 1) % m];
        push(sDirs, inA, outB, outA, inA, inB, outB);
        sIsTop.push(bowl[k].v, bowl[k + 1].v, bowl[k + 1].v, bowl[k].v, bowl[k].v, bowl[k + 1].v);
      }
    }

    // Pad (pentagons have fewer perimeter points) with degenerate vertices at
    // the centre so every slot has the same vertex count.
    while (sIsTop.length < SURFACE_VERTS_PER_SLOT) { push(sDirs, c); sIsTop.push(1); }
    push(hDirs, c);
    hGlow.push(radiusRad); // the circle's angular radius, for the selected ring

    tpl = {
      center: c,
      sDirs: Float32Array.from(sDirs),
      sIsTop: Float32Array.from(sIsTop),
      hDirs: Float32Array.from(hDirs),
      hGlow: Float32Array.from(hGlow),
    };
    cellTemplates.set(cellId, tpl);
    if (cellTemplates.size > MAX_CELL_TEMPLATES) cellTemplates.delete(cellTemplates.keys().next().value);
    return tpl;
  };

  const fillVec3 = (arr, from, count, v) => {
    for (let i = 0; i < count; i++) {
      const j = (from + i) * 3;
      arr[j] = v.x; arr[j + 1] = v.y; arr[j + 2] = v.z;
    }
  };

  // Slot pool: hexes live in fixed-size slots of persistent buffers. Adding
  // a hex writes one slot and uploads just the touched range; removing one
  // frees its slot (presence 0 collapses it). Buffers grow by doubling.
  const INITIAL_SLOTS = 512;
  const slotOfCell = new Map();
  let freeSlots = [];
  let highWater = 0;               // slots [0, highWater) have been used
  let capacity = 0;

  const SURFACE_ATTRS = [["position", 3], ["aCellId", 1], ["aIsTop", 1], ["aCenter", 3], ["aSlot", 1]];
  const HALO_ATTRS = [["position", 3], ["aCellId", 1], ["aGlow", 1], ["aCenter", 3], ["aSlot", 1]];

  const growGeometry = (old, attrs, vertsPerSlot, cap) => {
    const geom = new THREE.BufferGeometry();
    for (const [name, size] of attrs) {
      const arr = new Float32Array(cap * vertsPerSlot * size);
      if (old) arr.set(old.getAttribute(name).array);
      const attr = new THREE.Float32BufferAttribute(arr, size);
      attr.setUsage(THREE.DynamicDrawUsage);
      geom.setAttribute(name, attr);
    }
    if (old) old.dispose();
    return geom;
  };

  const grow = (needed) => {
    const cap = Math.max(needed, capacity * 2, INITIAL_SLOTS);
    surfaceGeometry = growGeometry(surfaceGeometry, SURFACE_ATTRS, SURFACE_VERTS_PER_SLOT, cap);
    haloGeometry = growGeometry(haloGeometry, HALO_ATTRS, HALO_VERTS_PER_SLOT, cap);
    const texH = Math.ceil(cap / SLOT_TEX_WIDTH);
    const data = new Float32Array(SLOT_TEX_WIDTH * texH * 4);
    if (slotData) data.set(slotData);
    slotData = data;
    if (slotTexture) slotTexture.dispose();
    slotTexture = new THREE.DataTexture(slotData, SLOT_TEX_WIDTH, texH, THREE.RGBAFormat, THREE.FloatType);
    slotTexture.needsUpdate = true;
    const stories = new Float32Array(SLOT_TEX_WIDTH * texH * 4);
    if (storyData) stories.set(storyData);
    storyData = stories;
    if (storyTexture) storyTexture.dispose();
    storyTexture = new THREE.DataTexture(storyData, SLOT_TEX_WIDTH, texH, THREE.RGBAFormat, THREE.FloatType);
    storyTexture.needsUpdate = true;
    const pulses = new Float32Array(SLOT_TEX_WIDTH * texH * 4);
    if (pulseData) pulses.set(pulseData);
    pulseData = pulses;
    if (pulseTexture) pulseTexture.dispose();
    pulseTexture = new THREE.DataTexture(pulseData, SLOT_TEX_WIDTH, texH, THREE.RGBAFormat, THREE.FloatType);
    pulseTexture.needsUpdate = true;
    capacity = cap;
  };

  const writeSlot = (slot, cellId, intId) => {
    const t = cellTemplate(cellId);
    const write = (geom, vertsPerSlot, fields) => {
      const from = slot * vertsPerSlot;
      for (const [name, size, src] of fields) {
        const arr = geom.getAttribute(name).array;
        if (src) arr.set(src, from * size);
        else if (name === "aCenter") fillVec3(arr, from, vertsPerSlot, t.center);
        else arr.fill(name === "aSlot" ? slot : intId, from, from + vertsPerSlot);
      }
    };
    write(surfaceGeometry, SURFACE_VERTS_PER_SLOT,
      [["position", 3, t.sDirs], ["aCellId", 1], ["aIsTop", 1, t.sIsTop], ["aCenter", 3], ["aSlot", 1]]);
    write(haloGeometry, HALO_VERTS_PER_SLOT,
      [["position", 3, t.hDirs], ["aCellId", 1], ["aGlow", 1, t.hGlow], ["aCenter", 3], ["aSlot", 1]]);
    surfaceCellIds[slot] = cellId;
    slotData[slot * 4] = -1; // force the next applyAltitudes to write it
    slotData[slot * 4 + 1] = 0;
    storyData[slot * 4] = t.center.x;
    storyData[slot * 4 + 1] = t.center.y;
    storyData[slot * 4 + 2] = t.center.z;
    storyData[slot * 4 + 3] = 0;
    storyTexture.needsUpdate = true;
    pulseData[slot * 4] = 0;
    pulseTexture.needsUpdate = true;
  };

  // Upload only the slots in [lo, hi] (or everything after a grow).
  const markUploaded = (geom, attrs, vertsPerSlot, lo, hi, full) => {
    for (const [name, size] of attrs) {
      const attr = geom.getAttribute(name);
      attr.updateRange.offset = full ? 0 : lo * vertsPerSlot * size;
      attr.updateRange.count = full ? -1 : (hi - lo + 1) * vertsPerSlot * size;
      attr.needsUpdate = true;
    }
  };

  // Make the pooled surface hold exactly `cellIds`.
  const setSurfaceCells = (cellIds, cellIdToInt) => {
    if (capacity === 0) grow(cellIds.length);
    const want = new Set(cellIds);
    for (const [id, slot] of slotOfCell) {
      if (want.has(id)) continue;
      slotOfCell.delete(id);
      surfaceCellIds[slot] = null;
      slotData[slot * 4 + 1] = 0;
      storyData[slot * 4 + 3] = 0;
      pulseData[slot * 4] = 0;
      freeSlots.push(slot);
      slotTexture.needsUpdate = true;
      storyTexture.needsUpdate = true;
      pulseTexture.needsUpdate = true;
    }

    const added = cellIds.filter((id) => !slotOfCell.has(id));
    let full = false;
    if (slotOfCell.size + added.length > capacity) {
      grow(slotOfCell.size + added.length);
      full = true;
    }
    let lo = Infinity;
    let hi = -1;
    for (const id of added) {
      const slot = freeSlots.length ? freeSlots.pop() : highWater++;
      slotOfCell.set(id, slot);
      writeSlot(slot, id, cellIdToInt ? (cellIdToInt.get(id) || 0) : 0);
      lo = Math.min(lo, slot);
      hi = Math.max(hi, slot);
    }
    if (added.length || full) {
      markUploaded(surfaceGeometry, SURFACE_ATTRS, SURFACE_VERTS_PER_SLOT, lo, hi, full);
      markUploaded(haloGeometry, HALO_ATTRS, HALO_VERTS_PER_SLOT, lo, hi, full);
      slotTexture.needsUpdate = true;
    }
    surfaceGeometry.setDrawRange(0, highWater * SURFACE_VERTS_PER_SLOT);
    haloGeometry.setDrawRange(0, highWater * HALO_VERTS_PER_SLOT);

    return {
      surfaceGeometry, haloGeometry, slotTexture, slotTexWidth: SLOT_TEX_WIDTH, surfaceRadius: radius,
      storyTexture, pulseTexture, storyCount: Math.min(highWater, MAX_NET_STORIES),
    };
  };

  // cellAlt: Float32Array of altitudes indexed like `cellIndex`.
  // presence: optional Map<cellId, 0..1> scaling each filled cell.
  // netWeights: optional Map<cellId, 0..1>, how strongly each lights the net.
  // newness: optional Map<cellId, 0..1>, how new each story is (it pulses).
  const applyAltitudes = (cellAlt, presence, netWeights, newness) => {
    const dPos = dotPositionAttr.array;
    const dAltPos = dotAltitudeAttr.array;
    for (let i = 0; i < numCells; i++) {
      const alt = cellAlt[i];
      const r = radius * (1 + alt);
      const j = i * 3;
      dPos[j]     = dotDirs[j]     * r;
      dPos[j + 1] = dotDirs[j + 1] * r;
      dPos[j + 2] = dotDirs[j + 2] * r;
      dAltPos[i] = alt;
    }
    dotPositionAttr.needsUpdate = true;
    dotAltitudeAttr.needsUpdate = true;

    if (surfaceGeometry) {
      let dirty = false;
      // Story texels first: centre direction scaled to length 1 + weight (so
      // the shader gets the importance weight from its length), presence in w.
      for (let s = 0; s < highWater; s++) {
        const id = surfaceCellIds[s];
        if (!id) continue;
        const p = presence ? presence.get(id) : undefined;
        const pres = p === undefined ? 1 : p;
        const j = s * 4;
        const weight = netWeights ? (netWeights.get(id) ?? 1) : 1;
        const len = Math.fround(1 + weight);
        const cLen = Math.hypot(storyData[j], storyData[j + 1], storyData[j + 2]);
        if (slotData[j + 1] !== pres || storyData[j + 3] !== Math.fround(pres) || Math.abs(cLen - len) > 1e-4) {
          slotData[j + 1] = pres;
          const k = len / cLen;
          storyData[j] *= k;
          storyData[j + 1] *= k;
          storyData[j + 2] *= k;
          storyData[j + 3] = pres;
          dirty = true;
        }
        const fresh = Math.fround(newness ? (newness.get(id) ?? 0) : 0);
        if (pulseData[j] !== fresh) {
          pulseData[j] = fresh;
          pulseTexture.needsUpdate = true;
        }
      }
      // Then each filled circle sits flat at the net's height at its centre,
      // so it rests on the net the dot shader draws around it.
      const count = Math.min(highWater, MAX_NET_STORIES);
      for (let s = 0; s < highWater; s++) {
        const id = surfaceCellIds[s];
        if (!id) continue;
        const j = s * 4;
        const len = Math.hypot(storyData[j], storyData[j + 1], storyData[j + 2]);
        const h = netHeightAt(storyData, count, storyData[j] / len, storyData[j + 1] / len, storyData[j + 2] / len, netReach);
        const alt = Math.fround(h * heatHeight);
        if (slotData[j] !== alt) {
          slotData[j] = alt;
          dirty = true;
        }
      }
      if (dirty) {
        slotTexture.needsUpdate = true;
        storyTexture.needsUpdate = true;
      }
    }
  };

  // A story's height on the net (globe radii), where its halo sits.
  const storyAltitude = (id) => {
    const slot = slotOfCell.get(id);
    return slot === undefined || !slotData ? null : slotData[slot * 4];
  };

  return { dotGeometry, applyAltitudes, setSurfaceCells, setHiddenCells, storyAltitude, cells, numCells };
}

// Shading shared by the grid lines (per pixel) and dots (per dot, in the
// vertex shader: it's constant across a dot, and running it per pixel over
// ~125k glowing dots was the most expensive thing on screen).
const HEX_SHADE_GLSL = /* glsl */ `
  #define MAX_WAVES 4
  uniform vec3 uColor;
  uniform float uOpacity;
  uniform float uRimBoost;
  uniform vec3 uHighlightPos;
  uniform vec3 uSelectedPos;
  uniform vec3 uPrevSelectedPos;
  uniform float uHighlightRadius;
  uniform float uCellScale; // grid cell size relative to resolution 3
  uniform float uGlobeRadius;

  uniform float uTime; uniform float uGlobalFade;
  uniform float uPressTime;
  uniform float uUnpressTime;
  uniform vec3 uWaveStartPos[MAX_WAVES];
  uniform float uWaveTime[MAX_WAVES];


  // How visible the net is here from the stories around it (0..1); set by
  // the dot vertex shader before it calls hexShade.
  float gNetVis = 0.0;

  vec4 hexShade(float dotFade, vec3 normalW, vec3 viewDirW, vec3 worldPos, float altitude, float tParam) {
    // Altitudes are in globe radii and shrink with the cell size at finer
    // grid levels; shade by the base level's equivalent.
    altitude /= uCellScale;
    float ndv = max(0.0, dot(normalW, viewDirW));
    float fres = pow(1.0 - ndv, 2.5);
    float intensity = clamp(0.85 + fres * uRimBoost, 0.0, 1.0);
    // The net's visibility comes only from the story heatmap (gNetVis), not
    // from the height field, whose plateau around big stories gave a full-
    // brightness area with a hard rim.
    float altFade = gNetVis;

    float maxWaveFactor = 0.0;
    for (int i = 0; i < MAX_WAVES; i++) {
      float waveAge = uTime - uWaveTime[i];
      if (waveAge < 0.0 || waveAge > 8.0) continue;

      float wavePos = 1.0 - waveAge * 0.3;
      vec3 nWaveStart = normalize(uWaveStartPos[i]);
      float wobble = sin(normalW.x * 8.0 + normalW.y * 12.0 + waveAge * 4.5) * 0.015;
      float cosAngle = dot(normalW, nWaveStart) + wobble;
      float angle = acos(clamp(cosAngle, -1.0, 1.0));

      float dynamicWidth = 0.05 + waveAge * 0.06;
      float waveFactor = smoothstep(wavePos - dynamicWidth, wavePos, cosAngle) * 
                         smoothstep(wavePos + dynamicWidth, wavePos, cosAngle);
      
      float energy = pow(clamp(1.0 - angle / 3.14159, 0.0, 1.0), 2.0);
      maxWaveFactor = max(maxWaveFactor, waveFactor * energy);
    }
    
    #ifdef USE_HEATMAP
      float t = smoothstep(0.0, 0.22, altitude);
      vec3 cCyan = vec3(0.302, 0.816, 1.0);
      vec3 cGreen = mix(vec3(0.0, 1.0, 0.0), cCyan, 0.4);
      vec3 cYellow = mix(vec3(1.0, 1.0, 0.0), cCyan, 0.4);
      vec3 cRed = mix(vec3(1.0, 0.0, 0.0), cCyan, 0.4);
      vec3 heatColor = mix(cCyan, cGreen, clamp(t * 3.0, 0.0, 1.0));
      heatColor = mix(heatColor, cYellow, clamp((t - 0.333) * 3.0, 0.0, 1.0));
      heatColor = mix(heatColor, cRed, clamp((t - 0.666) * 3.0, 0.0, 1.0));
      vec3 baseColor = heatColor * intensity;
    #elif defined(USE_ELECTRIC)
      float t = smoothstep(0.0, 0.22, altitude);
      vec3 cBottom = vec3(0.05, 0.2, 1.0); // Deeper, bluer
      vec3 cMiddle = vec3(0.302, 0.816, 1.0); // Current Cyan
      vec3 cTop = vec3(0.7, 0.9, 1.0); // Whiter Cyan
      vec3 electricColor;
      if (t < 0.5) {
        electricColor = mix(cBottom, cMiddle, t * 2.0);
      } else {
        electricColor = mix(cMiddle, cTop, (t - 0.5) * 2.0);
      }
      vec3 baseColor = electricColor * intensity;
    #else
      vec3 baseColor = uColor * intensity;
      float heat01 = smoothstep(0.0, 0.22, altitude);
      baseColor = mix(baseColor, vec3(1.0), heat01 * 0.55);
    #endif

    vec3 projWorld = normalize(worldPos) * uGlobeRadius;
    float d = distance(projWorld, uHighlightPos);
    float highlight = smoothstep(uHighlightRadius, 0.0, d);
    
    vec3 finalColor = mix(baseColor, vec3(1.0), highlight * 0.8);
    vec3 waveCol = mix(uColor, vec3(1.0), 0.8);
    finalColor = mix(finalColor, waveCol, maxWaveFactor * 0.475);
    
    float finalOpacity = mix(uOpacity * altFade, uOpacity, highlight) * dotFade;
    finalOpacity = mix(finalOpacity, 1.0 * dotFade, maxWaveFactor * 0.5);
    
    // Clearance mask — only active after an explicit selection (uSelectedPos),
    // not on hover, so the grid fades only when the user snaps to a hex.
    float gridClearance = 1.0;
    if (uSelectedPos.x < 1e5) {
      float pressAge = uTime - uPressTime;
      float t = clamp(pressAge / 0.45, 0.0, 1.0);
      float expansion = t * t;
      float dClearance = distance(projWorld, uSelectedPos);
      float clearance = smoothstep(8.0 * uCellScale * expansion, 28.0 * uCellScale * expansion, dClearance);
      // Tie clearance intensity to uGlobalFade (1.0 when dragging = no clearance)
      clearance = mix(1.0, clearance, 1.0 - (uGlobalFade - 0.2) / 0.8);
      gridClearance *= mix(1.0, clearance, expansion);
    }
    if (uPrevSelectedPos.x < 1e5) {
      float unpressAge = uTime - uUnpressTime;
      if (unpressAge >= 0.0 && unpressAge < 0.45) {
        float t = 1.0 - clamp(unpressAge / 0.45, 0.0, 1.0);
        float expansion = t * t;
        float dClearance = distance(projWorld, uPrevSelectedPos);
        float clearance = smoothstep(8.0 * uCellScale * expansion, 28.0 * uCellScale * expansion, dClearance);
        // Tie clearance intensity to uGlobalFade
        clearance = mix(1.0, clearance, 1.0 - (uGlobalFade - 0.2) / 0.8);
        gridClearance *= mix(1.0, clearance, expansion);
      }
    }
    finalOpacity *= gridClearance;

    // Rounded corners: fade each edge line near its endpoints so corners
    // appear soft rather than sharp hexagonal vertices.
    #ifndef IS_POINTS
      float cornerFade = smoothstep(0.0, 0.13, tParam) * smoothstep(1.0, 0.87, tParam);
      finalOpacity *= cornerFade;
    #endif

    return vec4(finalColor, finalOpacity);
  }
`;

const VERTEX_SHADER = /* glsl */ `
  uniform float uPointSize;
  #ifdef IS_POINTS
    ${HEX_SHADE_GLSL}
    varying vec4 vDotShade;
    uniform sampler2D uStoryData;  // per story: (centre direction, presence)
    uniform float uStoryTexWidth;
    uniform int uStoryCount;
    uniform float uNetReach;       // radians
    uniform float uNetRadius;      // the net's resting shell radius (world units)
    uniform float uHeatHeight;     // height at full heatmap strength (globe radii)
    uniform sampler2D uStoryPulse; // per story: (newness 0..1, -, -, -)
    uniform float uStoryDotRad;    // radians: a story's own dot lies this close
    // Brightness of new stories' pulse rings here (0..1); set by netVisibility.
    float gPulse = 0.0;
    // Whether this dot is a story's own dot (its presence, 0..1), and that
    // story's importance weight; set by netVisibility.
    float gStory = 0.0;
    float gStoryWeight = 0.0;
    // A heatmap: every story adds density around it, and the
    // net's visibility is the summed density (capped at 1). Density from
    // neighbouring stories adds up, so zones merge smoothly instead of each
    // story showing as its own circle. A lone story peaks at 1 at its centre
    // and fades from there, half visible at uNetReach, and reaches exactly
    // zero at 2.5 × uNetReach: (1 - d/D)^p, which tapers gradually but, unlike
    // an exponential, doesn't leave a faint tail that sums to cover the globe.
    //
    // In the same pass, height: each story raises a smooth hill as tall as its
    // importance weight (times presence), and where hills overlap the tallest
    // wins rather than adding up — so height shows where the big stories are,
    // while the summed heatmap (glow and dot size) shows where there's a lot
    // going on. Written to 'height' as 0..1 of uHeatHeight.
    float netVisibility(vec3 dir, out float height) {
      float edge = uNetReach * 2.5;            // where a story adds nothing
      const float SHAPE = 1.357;               // (1 - 1/2.5)^SHAPE = 0.5 at R
      // Pulses run a little past a story's zone, so the cutoff is wider.
      float cosCutoff = cos(edge * ${PULSE_OVERSHOOT});
      float density = 0.0;
      height = 0.0;
      gPulse = 0.0;
      gStory = 0.0;
      gStoryWeight = 0.0;
      float cosStoryDot = cos(uStoryDotRad);
      for (int s = 0; s < ${MAX_NET_STORIES}; s++) {
        if (s >= uStoryCount) break;
        vec4 st = texelFetch(uStoryData, ivec2(s % int(uStoryTexWidth), s / int(uStoryTexWidth)), 0);
        if (st.w <= 0.0) continue;
        float centreLen = length(st.xyz);
        float weight = centreLen - 1.0;          // importance, 0..1
        float c = dot(dir, st.xyz / centreLen);
        if (c <= cosCutoff) continue;
        // Bigger stories reach further, not just brighter: the radius runs
        // from MIN_REACH of the full reach (weakest) to all of it (strongest).
        // Presence (growing in / shrinking out) scales both, so nothing pops.
        float storyEdge = edge * mix(${NET_MIN_REACH}, 1.0, weight) * st.w;
        if (c > cosStoryDot && st.w > gStory) {
          gStory = st.w;
          gStoryWeight = weight;
        }
        float d = acos(clamp(c, -1.0, 1.0));
        // New stories send out rings of light every PULSE_PERIOD seconds,
        // each staggered by the story's slot so they don't all pulse at once.
        // A ring travels from the story to PULSE_OVERSHOOT × its zone's edge
        // over PULSE_TRAVEL seconds, fading as it goes.
        if (d < storyEdge * ${PULSE_OVERSHOOT}) {
          float newness = texelFetch(uStoryPulse, ivec2(s % int(uStoryTexWidth), s / int(uStoryTexWidth)), 0).r;
          if (newness > 0.0) {
            float cycle = fract(uTime / ${PULSE_PERIOD} + fract(float(s) * 0.6180339));
            float travel = cycle * ${PULSE_PERIOD} / ${PULSE_TRAVEL};
            if (travel < 1.0) {
              float ringR = travel * storyEdge * ${PULSE_OVERSHOOT};
              float ringW = storyEdge * 0.15;
              float ring = 1.0 - smoothstep(0.0, ringW, abs(d - ringR));
              gPulse = max(gPulse, newness * st.w * ring * (1.0 - travel));
            }
          }
        }
        if (d >= storyEdge) continue;
        // Strength has a floor too, so even a lone minor story shows a patch
        // of net (about 36% at its centre, against ~63% for the biggest).
        float t = 1.0 - d / storyEdge;
        density += mix(${NET_MIN_STRENGTH}, 1.0, weight) * st.w * pow(t, SHAPE);
        // Big stories are volcanoes: t^PEAK is steepest at the summit and
        // flares out towards the foot (concave flanks), so they rise to a
        // sharp peak, and still meet the ground without a step. Small ones
        // are soft mounds (smoothstep), blending between the two by weight
        // (netHillShape, keep in sync).
        // Height grows with a power of the weight, so minor stories stay
        // nearly flat and only the important ones rise into peaks.
        float mound = t * t * (3.0 - 2.0 * t);
        float hill = mix(mound, pow(t, ${NET_PEAK_SHARPNESS}), smoothstep(${NET_MOUND_BELOW}, ${NET_VOLCANO_ABOVE}, weight));
        height = max(height, pow(weight, ${NET_HEIGHT_CONTRAST}) * st.w * hill);
      }
      // Soft saturation (no clamp, so overlapping stories never flatten into
      // a plateau), then a power so brightness keeps falling off gradually
      // and faint overlaps far from any story are barely visible.
      return pow(1.0 - exp(-1.5 * density), 1.6);
    }
  #else
    #define MAX_WAVES 4
    uniform float uCellScale;
    uniform float uTime; uniform float uGlobalFade;
    uniform float uGlobeRadius;
    uniform vec3 uWaveStartPos[MAX_WAVES];
    uniform float uWaveTime[MAX_WAVES];
  #endif
  attribute float aAltitude;
  attribute float aT;
  #ifdef IS_POINTS
    attribute float aHidden;
    varying float vHidden;
  #endif
  varying float vAltitude;
  varying float vT;
  varying vec3 vNormalW;
  varying vec3 vViewDirW;
  varying vec3 vWorldPos;

  void main() {
    vAltitude = aAltitude;
    vT = aT;
    vec3 basePos = position;
    #ifdef IS_POINTS
      vHidden = aHidden;
    #endif

    vec3 normal = normalize(position);
    vNormalW = normalize(mat3(modelMatrix) * normal);

    #ifdef IS_POINTS
      // Dots under a filled circle, or on the far side of the globe, are
      // dropped before the heatmap loop (the costly part).
      vec3 shellWorld = (modelMatrix * vec4(normal * uNetRadius, 1.0)).xyz;
      vec3 shellSight = shellWorld - cameraPosition;
      float shellAlong = clamp(-dot(cameraPosition, shellSight) / dot(shellSight, shellSight), 0.0, 1.0);
      if (aHidden > 0.5 || (shellAlong < 1.0 && length(cameraPosition + shellSight * shellAlong) < uGlobeRadius * 0.999)) {
        gl_PointSize = 0.0;
        gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
        return;
      }
      // Opacity and dot size come from the heatmap, height from importance.
      float importanceHeight;
      gNetVis = netVisibility(normal, importanceHeight);
      vAltitude = importanceHeight * uHeatHeight;
      basePos = normal * (uNetRadius + uGlobeRadius * vAltitude);
    #endif
    
    float totalWaveFactor = 0.0;
    for (int i = 0; i < MAX_WAVES; i++) {
      float waveAge = uTime - uWaveTime[i];
      if (waveAge < 0.0 || waveAge > 8.0) continue;

      float wavePos = 1.0 - waveAge * 0.3;
      vec3 nWaveStart = normalize(uWaveStartPos[i]);
      float dynamicWidth = 0.05 + waveAge * 0.06;
      
      float wobble = sin(vNormalW.x * 8.0 + vNormalW.y * 12.0 + waveAge * 4.5) * 0.015;
      float cosAngle = dot(vNormalW, nWaveStart) + wobble;
      float angle = acos(clamp(cosAngle, -1.0, 1.0));

      float waveFactor = smoothstep(wavePos - dynamicWidth, wavePos, cosAngle) * 
                         smoothstep(wavePos + dynamicWidth, wavePos, cosAngle);
      
      float energy = pow(clamp(1.0 - angle / 3.14159, 0.0, 1.0), 2.0);
      totalWaveFactor += waveFactor * energy;
    }

    // Height displacement (damped by existing altitude)
    // Displacements scale with the grid level's cell size.
    float altitudeDamping = clamp(1.0 - vAltitude / uCellScale * 4.0, 0.0, 1.0);
    float waveDisplacement = totalWaveFactor * 4.0 * uCellScale * altitudeDamping;

    vec3 displacedPos = basePos + normal * waveDisplacement;

    vec4 worldPos = modelMatrix * vec4(displacedPos, 1.0);
    vWorldPos = worldPos.xyz;
    vViewDirW = normalize(cameraPosition - worldPos.xyz);
    
    vec4 mvPosition = viewMatrix * worldPos;
    gl_Position = projectionMatrix * mvPosition;
    
    // Size attenuation
    gl_PointSize = uPointSize * (300.0 / -mvPosition.z);
    #ifdef IS_POINTS
      gl_PointSize *= DOT_EXTENT;
      // Dots behind the globe: the sight line meets the planet first. Culled
      // here, since the fragment shader's discard defeats early depth tests.
      vec3 sight = worldPos.xyz - cameraPosition;
      float along = clamp(-dot(cameraPosition, sight) / dot(sight, sight), 0.0, 1.0);
      bool behind = along < 1.0 && length(cameraPosition + sight * along) < uGlobeRadius * 0.999;
      if (aHidden > 0.5 || behind) {
        gl_PointSize = 0.0;
        gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
      } else {
        // Dots shrink as the net fades out, down to NET_MIN_DOT_SIZE of full
        // size; a passing pulse swells them back up.
        gl_PointSize *= mix(${NET_MIN_DOT_SIZE}, 1.0, max(sqrt(gNetVis), gPulse));
        vDotShade = hexShade(1.0, vNormalW, vViewDirW, vWorldPos, vAltitude, 0.0);
        // A pulse lights the dots it passes, whitening them and lifting their
        // opacity, even where the net itself is faint.
        vDotShade.rgb = mix(vDotShade.rgb, mix(uColor, vec3(1.0), 0.6) * 1.5, gPulse);
        vDotShade.a = max(vDotShade.a, uOpacity * gPulse);
        // A story's own dot: bigger (by importance) and fully opaque, in the
        // net's colour, or white while hovered (uHighlightPos is the hovered
        // story's centre); it grows and fades with the story's presence.
        if (gStory > 0.0) {
          float storySize = mix(${STORY_DOT_SIZE[0]}, ${STORY_DOT_SIZE[1]}, gStoryWeight);
          gl_PointSize *= mix(1.0, storySize, gStory);
          bool hovered = uHighlightPos.x < 1e5 && dot(normal, normalize(uHighlightPos)) > cos(uStoryDotRad);
          vDotShade.rgb = mix(vDotShade.rgb, hovered ? vec3(1.0) : uColor, gStory);
          vDotShade.a = mix(vDotShade.a, uOpacity, gStory);
        }
      }
    #endif
  }
`;

const FRAGMENT_SHADER = /* glsl */ `
  ${HEX_SHADE_GLSL}
  varying float vAltitude;
  varying float vT;
  varying vec3 vNormalW;
  varying vec3 vViewDirW;
  varying vec3 vWorldPos;
  #ifdef IS_POINTS
    varying float vHidden;
    varying vec4 vDotShade;
  #endif
  void main() {
    #ifdef IS_POINTS
      if (vHidden > 0.5) discard;
      // The sprite covers the dot out to where its glow falls below 1%
      // (DOT_EXTENT of the original radius); r is in the original units.
      float r = distance(gl_PointCoord, vec2(0.5)) * DOT_EXTENT;
      if (r > 0.5 * DOT_EXTENT) discard;
      float core = 1.0 - smoothstep(0.04, 0.08, r);
      float glow = pow(1.0 - smoothstep(0.0, 0.5, r), 3.0);
      float dotFade = core + glow * 0.75;
      gl_FragColor = vec4(vDotShade.rgb, vDotShade.a * dotFade);
    #else
      gl_FragColor = hexShade(1.0, vNormalW, vViewDirW, vWorldPos, vAltitude, vT);
    #endif
  }
`;

export function createHexWireframeMaterial({
  color = 0x4dd0ff,
  opacity = 0.65,
  rimBoost = 1.6,
  lineWidth = 2,
  pointSize = 4.0,
  isPoints = false,
  useHeatmap = false,
  useElectric = false,
  globeRadius = 100.0,
  highlightRadius = 18.0,
  cellScale = 1.0,
  netRadius = null, // the dots' resting shell radius (defaults to the globe's)
} = {}) {
  // Dots: the glow is under 1% beyond 0.7 of the sprite's original radius,
  // so the sprite is trimmed to that (half the pixels).
  const defines = isPoints ? { IS_POINTS: "", DOT_EXTENT: "0.7" } : {};
  if (useHeatmap) defines.USE_HEATMAP = "";
  if (useElectric) defines.USE_ELECTRIC = "";
  return new THREE.ShaderMaterial({
    uniforms: {
      uColor: { value: new THREE.Color(color) },
      uOpacity: { value: opacity },
      uRimBoost: { value: rimBoost },
      uHighlightPos: { value: new THREE.Vector3(1e6, 1e6, 1e6) },
      uSelectedPos: { value: new THREE.Vector3(1e6, 1e6, 1e6) },
      uPrevSelectedPos: { value: new THREE.Vector3(1e6, 1e6, 1e6) },
      uHighlightRadius: { value: highlightRadius },
      uCellScale: { value: cellScale },
      uGlobeRadius: { value: globeRadius },
      uPointSize: { value: pointSize },
      uTime: { value: 0.0 },
      uPressTime: { value: -100.0 },
      uUnpressTime: { value: -100.0 },
      uGlobalFade: { value: 1.0 },
      uWaveStartPos: { value: Array(4).fill(0).map(() => new THREE.Vector3(0, 0, 1)) },
      uWaveTime: { value: Array(4).fill(-100.0) },
      uStoryData: { value: null },
      uStoryPulse: { value: null },
      uStoryTexWidth: { value: 1 },
      uStoryCount: { value: 0 },
      uNetReach: { value: NET_REACH_RAD * cellScale },
      uNetRadius: { value: netRadius ?? globeRadius },
      uHeatHeight: { value: NET_HEAT_HEIGHT * cellScale },
      uStoryDotRad: { value: STORY_DOT_RAD * cellScale },
    },
    defines: defines,
    vertexShader: VERTEX_SHADER,
    fragmentShader: FRAGMENT_SHADER,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    linewidth: lineWidth,
  });
}

const BEAM_VERTEX_SHADER = /* glsl */ `
  attribute float aCellId;
  attribute float aIsTop;
  varying float vCellId;
  varying float vFade;
  void main() {
    vCellId = aCellId;
    vFade = aIsTop;
    vec4 worldPosition = modelMatrix * vec4(position, 1.0);
    gl_Position = projectionMatrix * viewMatrix * worldPosition;
  }
`;

const BEAM_FRAGMENT_SHADER = /* glsl */ `
  uniform vec3 uColor;
  uniform float uOpacity;
  uniform float uSelectedCellId;
  varying float vCellId;
  varying float vFade;
  void main() {
    float fade = pow(vFade, 0.5);
    float highlight = (abs(vCellId - uSelectedCellId) < 0.5 && uSelectedCellId > 0.0) ? 1.0 : 0.0;
    
    // Base light is softer
    vec3 baseLight = mix(uColor, vec3(1.0), 0.3);
    
    vec3 finalColor = mix(baseLight, vec3(1.0), highlight);
    float finalOpacity = mix(uOpacity * 0.3, min(1.0, uOpacity * 2.0), highlight) * fade;
    
    gl_FragColor = vec4(finalColor, finalOpacity);
  }
`;

export function createBeamMaterial({
  color = 0x4dd0ff,
  opacity = 0.5,
} = {}) {
  return new THREE.ShaderMaterial({
    uniforms: {
      uColor: { value: new THREE.Color(color) },
      uOpacity: { value: opacity },
      uSelectedCellId: { value: 0.0 },
    },
    vertexShader: BEAM_VERTEX_SHADER,
    fragmentShader: BEAM_FRAGMENT_SHADER,
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
    blending: THREE.AdditiveBlending,
  });
}

const SLOT_POSITION_GLSL = /* glsl */ `
  uniform sampler2D uSlotData;   // per hex: (top altitude, presence)
  uniform float uSlotTexWidth;
  uniform float uSurfaceRadius;
  attribute float aSlot;
  // Unit direction → world position for this vertex's hex: shrunk toward the
  // hex centre by its presence (0..1) and lifted to its top altitude.
  float slotPresence() {
    ivec2 uv = ivec2(int(mod(aSlot, uSlotTexWidth)), int(floor(aSlot / uSlotTexWidth)));
    return texelFetch(uSlotData, uv, 0).y;
  }
  vec3 slotPosition(vec3 dir, vec3 center, float altOffset) {
    ivec2 uv = ivec2(int(mod(aSlot, uSlotTexWidth)), int(floor(aSlot / uSlotTexWidth)));
    vec2 d = texelFetch(uSlotData, uv, 0).xy;
    vec3 p = normalize(center + (dir - center) * max(d.y, 1e-4));
    return p * uSurfaceRadius * (1.0 + d.x + altOffset);
  }
`;

const SURFACE_VERTEX_SHADER = /* glsl */ `
  ${SLOT_POSITION_GLSL}
  uniform float uButtonDepth;
  uniform float uDomeHeight;
  uniform float uGlobeRadius;
  uniform float uSelectedCellId;
  uniform float uPrevSelectedCellId;
  uniform float uHoveredCellId;
  uniform float uPressedCellId;
  uniform float uPressTime;
  uniform float uUnpressTime;
  uniform float uTime; uniform float uGlobalFade;
  attribute float aCellId;
  attribute float aIsTop;
  attribute vec3 aCenter;
  varying float vCellId;
  varying float vAltitude;
  varying vec3 vWorldPos;
  void main() {
    vCellId = aCellId;

    // aIsTop: on top 1 at the edge up to 2 at the middle of the dome; on the
    // underside 0 at the edge down to -1 at the middle of the bowl.
    bool onTop = aIsTop > 0.5;
    float dome = onTop ? clamp(aIsTop - 1.0, 0.0, 1.0) : 0.0;
    float bowl = onTop ? 0.0 : clamp(-aIsTop, 0.0, 1.0);
    vec3 basePos = slotPosition(position, aCenter, onTop ? dome * uDomeHeight : -(uButtonDepth + bowl * uDomeHeight));
    vec3 pos = basePos;

    bool isSelected = (abs(aCellId - uSelectedCellId) < 0.5 && uSelectedCellId > 0.0);
    bool isPrevSelected = (abs(aCellId - uPrevSelectedCellId) < 0.5 && uPrevSelectedCellId > 0.0);
    bool isHovered  = (abs(aCellId - uHoveredCellId)  < 0.5 && uHoveredCellId  > 0.0);
    float distToSurface = length(basePos) - uGlobeRadius;

    if (isSelected) {
      // Full snap-to-surface over 0.22s.
      float age = uTime - uPressTime;
      float t = clamp(age / 0.22, 0.0, 1.0);
      float press = t * t * t;
      pos = basePos - normalize(basePos) * (distToSurface * press);
      
      // Expansion: 100% larger when selected
      vec3 centerPos = aCenter * uGlobeRadius;
      pos = centerPos + (pos - centerPos) * (1.0 + t * 1.0);
    } else if (isPrevSelected) {
      float age = uTime - uUnpressTime;
      if (age >= 0.0 && age < 0.22) {
        float t = 1.0 - clamp(age / 0.22, 0.0, 1.0);
        
        // Target state when unpress is finished (t=0)
        float targetSink = isHovered ? 0.25 : 0.0;
        float targetExpand = isHovered ? 0.05 : 0.0;
        
        float currentSink = mix(targetSink, 1.0, t);
        float currentExpand = mix(targetExpand, 1.0, t);

        pos = basePos - normalize(basePos) * (distToSurface * currentSink);
        vec3 centerPos = aCenter * uGlobeRadius;
        pos = centerPos + (pos - centerPos) * (1.0 + currentExpand);
      }
    } else if (isHovered) {
      // Subtle hover dip — 25% of full press, immediate.
      pos = basePos - normalize(basePos) * (distToSurface * 0.25);
      
      // Expansion: 5% larger on hover
      vec3 centerPos = aCenter * uGlobeRadius;
      pos = centerPos + (pos - centerPos) * 1.05;
    }

    vAltitude = max(0.0, length(pos) / uGlobeRadius - 1.0);
    vec4 worldPos = modelMatrix * vec4(pos, 1.0);
    vWorldPos = worldPos.xyz;
    gl_Position = projectionMatrix * viewMatrix * worldPos;
  }
`;

const SURFACE_FRAGMENT_SHADER = /* glsl */ `
  uniform vec3 uColor;
  uniform float uOpacity;
  uniform float uSelectedCellId;
  uniform float uPrevSelectedCellId;
  uniform float uHoveredCellId;
  uniform float uPressedCellId;
  uniform float uPressTime;
  uniform float uUnpressTime;
  uniform float uTime; uniform float uGlobalFade; uniform float uGlobeRadius;
  uniform float uCellScale;
  uniform vec3 uSelectedPos;
  uniform vec3 uPrevSelectedPos;
  varying float vCellId;
  varying float vAltitude;
  varying vec3 vWorldPos;
  void main() {
    // Pure emission: the category colour, unshaded.
    vec3 baseColor = uColor;

    // Persistent selection highlight — settles to steady white glow.
    float isSelected = (abs(vCellId - uSelectedCellId) < 0.5 && uSelectedCellId > 0.0) ? 1.0 : 0.0;
    float isPrevSelected = (abs(vCellId - uPrevSelectedCellId) < 0.5 && uPrevSelectedCellId > 0.0) ? 1.0 : 0.0;
    float isHovered  = (abs(vCellId - uHoveredCellId)  < 0.5 && uHoveredCellId  > 0.0) ? 1.0 : 0.0;
    
    float unpressAge = uTime - uUnpressTime;
    float unpressFade = (isPrevSelected > 0.5 && unpressAge >= 0.0 && unpressAge < 0.22) ? (1.0 - clamp(unpressAge / 0.22, 0.0, 1.0)) : 0.0;
    float effectiveSelected = max(isSelected, unpressFade);

    // Animated press-flash: white blooms AFTER the hex bottoms out.
    float pressFlash = 0.0;
    bool isThisPressed = (abs(vCellId - uPressedCellId) < 0.5 && uPressedCellId > 0.0);
    float pressAge = uTime - uPressTime;
    if (isThisPressed && pressAge >= 0.0 && pressAge < 1.5) {
      float rampIn  = smoothstep(0.10, 0.22, pressAge);
      float rampOut = 1.0 - smoothstep(0.3, 0.9, pressAge);
      pressFlash = rampIn * rampOut;
    }

    // Hover: subtle white tint, suppressed while selected/pressed.
    float hoverAmt = isHovered * (1.0 - max(effectiveSelected, pressFlash));

    // Blend: base → hover white → selected/flash white.
    float highlight = max(effectiveSelected * 0.8, pressFlash);
    vec3 finalColor = mix(baseColor, vec3(1.0), highlight);
    finalColor = mix(finalColor, vec3(1.0), hoverAmt * 0.55);
    float finalOpacity = mix(uOpacity, min(1.0, uOpacity * 2.0), max(effectiveSelected, pressFlash));
    finalOpacity = mix(finalOpacity, min(1.0, finalOpacity * 1.5), hoverAmt);


    // Fade out unselected hexes
    // if (effectiveSelected < 0.5) {
    //   finalOpacity *= uGlobalFade;
    // }

    // Radial fade for surrounding hexes to match dot/cloud clearance
    float hexClearance = 1.0;
    if (uSelectedPos.x < 1e5) {
      float pressAge = uTime - uPressTime;
      float t = clamp(pressAge / 0.45, 0.0, 1.0);
      float expansion = t * t;
      float d = distance(normalize(vWorldPos) * uGlobeRadius, uSelectedPos);
      float localClearance = smoothstep(8.0 * uCellScale * expansion, 28.0 * uCellScale * expansion, d);
      // Tie clearance intensity to uGlobalFade
      localClearance = mix(1.0, localClearance, 1.0 - (uGlobalFade - 0.2) / 0.8);
      // Keep the selected hex itself fully visible
      localClearance = mix(localClearance, 1.0, isSelected);
      hexClearance *= mix(1.0, localClearance, expansion);
    }
    if (uPrevSelectedPos.x < 1e5) {
      float unpressAge = uTime - uUnpressTime;
      if (unpressAge >= 0.0 && unpressAge < 0.45) {
        float t = 1.0 - clamp(unpressAge / 0.45, 0.0, 1.0);
        float expansion = t * t;
        float d = distance(normalize(vWorldPos) * uGlobeRadius, uPrevSelectedPos);
        float localClearance = smoothstep(8.0 * uCellScale * expansion, 28.0 * uCellScale * expansion, d);
        // Tie clearance intensity to uGlobalFade
        localClearance = mix(1.0, localClearance, 1.0 - (uGlobalFade - 0.2) / 0.8);
        // Keep the previous selection visible while it unpresses
        localClearance = mix(localClearance, 1.0, isPrevSelected);
        hexClearance *= mix(1.0, localClearance, expansion);
      }
    }
    finalOpacity *= hexClearance;

    // Fade to zero opacity as it reaches the surface (matching the vertex shader's 0.22s ramp).
    bool isActuallySelected = (abs(vCellId - uSelectedCellId) < 0.5 && uSelectedCellId > 0.0);
    if (isActuallySelected) {
      float pressAge = uTime - uPressTime;
      float t = clamp(pressAge / 0.22, 0.0, 1.0);
      float press = t * t * t;
      finalOpacity *= (1.0 - press);
    }

    gl_FragColor = vec4(finalColor, finalOpacity);
  }
`;

export function createHexSurfaceMaterial({
  color = 0x4dd0ff,
  opacity = 0.3,
  rimBoost = 1.6,
  globeRadius = 100.0,
  useHeatmap = false,
  useElectric = false,
  cellScale = 1.0,
} = {}) {
  const defines = {};
  if (useHeatmap) defines.USE_HEATMAP = "";
  if (useElectric) defines.USE_ELECTRIC = "";
  return new THREE.ShaderMaterial({
    uniforms: {
      uCellScale: { value: cellScale },
      uColor: { value: new THREE.Color(color) },
      uOpacity: { value: opacity },
      uRimBoost: { value: rimBoost },
      uSelectedCellId: { value: 0.0 },
      uPrevSelectedCellId: { value: 0.0 },
      uHoveredCellId: { value: 0.0 },
      uPressedCellId: { value: 0.0 },
      uPressTime: { value: -100.0 },
      uUnpressTime: { value: -100.0 },
      uGlobalFade: { value: 1.0 },
      uTime: { value: 0.0 },
      uGlobeRadius: { value: globeRadius },
      uSlotData: { value: null },
      uSlotTexWidth: { value: 1 },
      uSurfaceRadius: { value: globeRadius },
      uButtonDepth: { value: BUTTON_DEPTH },
      uDomeHeight: { value: DOME_HEIGHT * cellScale },
    },
    vertexShader: SURFACE_VERTEX_SHADER,
    fragmentShader: SURFACE_FRAGMENT_SHADER,
    defines: defines,
    // Normal blending with depth writes so the filled hexes cover the globe
    // (and each other) instead of only adding light onto it.
    transparent: true,
    depthWrite: true,
    side: THREE.FrontSide,
    blending: THREE.NormalBlending,
  });
}

const HALO_VERTEX_SHADER = /* glsl */ `
  ${SLOT_POSITION_GLSL}
  uniform float uGlobeRadius;
  uniform float uSelectedCellId;
  uniform float uPrevSelectedCellId;
  uniform float uHoveredCellId;
  uniform float uPressTime;
  uniform float uUnpressTime;
  uniform float uTime; uniform float uGlobalFade;
  uniform float uPointSize;
  uniform float uHaloLift;
  uniform float uViewportH;
  uniform float uPixelRatio;
  attribute float aCellId;
  attribute float aGlow;
  attribute vec3 aCenter;
  varying float vSel;
  varying float vSpritePx;
  varying float vRestPx;
  varying float vRingPx;
  void main() {
    // Selected (and easing back out when let go): the ring, which settles
    // in from a little wider and fades in as it does.
    bool isSel = abs(aCellId - uSelectedCellId) < 0.5 && uSelectedCellId > 0.0;
    bool isPrev = abs(aCellId - uPrevSelectedCellId) < 0.5 && uPrevSelectedCellId > 0.0;
    float prevAge = uTime - uUnpressTime;
    float prevFade = (isPrev && prevAge >= 0.0 && prevAge < 0.22) ? 1.0 - clamp(prevAge / 0.22, 0.0, 1.0) : 0.0;
    float settle = isSel ? clamp((uTime - uPressTime) / ${RING_SETTLE_S}, 0.0, 1.0) : 1.0;
    float eased = 1.0 - pow(1.0 - settle, 3.0);
    float fadeIn = isSel ? smoothstep(0.0, 0.4, settle) : 1.0;
    vSel = (isSel ? 1.0 : prevFade) * fadeIn;
    // The sprite floats above the story's place on the net.
    vec3 pos = slotPosition(position, aCenter, uHaloLift);
    vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);
    gl_Position = projectionMatrix * mvPosition;
    // Sizes in drawing-buffer pixels: the ring's radius follows the story's
    // circle (aGlow is its angular radius) within the on-screen limits.
    float pxPerUnit = 0.5 * uViewportH * projectionMatrix[1][1] / -mvPosition.z;
    float ringPx = clamp(${RING_RADII.toFixed(2)} * aGlow * uSurfaceRadius * pxPerUnit,
                         ${RING_MIN_PX.toFixed(1)} * uPixelRatio, ${RING_MAX_PX.toFixed(1)} * uPixelRatio);
    vRingPx = ringPx * mix(${RING_SETTLE_FROM}, 1.0, eased);
    vRestPx = uPointSize * (300.0 / -mvPosition.z);
    float ringSprite = 2.0 * (vRingPx + ${RING_WIDTH_PX.toFixed(1)} * uPixelRatio + 2.0);
    // It grows and shrinks with the story's presence.
    vSpritePx = (vSel > 0.0 ? max(vRestPx, ringSprite) : vRestPx) * slotPresence();
    gl_PointSize = vSpritePx;
  }
`;

const HALO_FRAGMENT_SHADER = /* glsl */ `
  uniform vec3 uColor;
  uniform float uOpacity;
  uniform float uPixelRatio;
  varying float vSel;
  varying float vSpritePx;
  varying float vRestPx;
  varying float vRingPx;
  void main() {
    float r = distance(gl_PointCoord, vec2(0.5));
    if (r > 0.5) discard;
    float px = r * vSpritePx; // distance from the centre in pixels
    // At rest: the story's faint glow, cubic falloff over its own sprite.
    float restR = px / max(vRestPx, 1e-3);
    float rest = restR < 0.5 ? pow(1.0 - smoothstep(0.0, 0.5, restR), 3.0) * ${HALO_REST_GLOW} : 0.0;
    // Selected: the ring, antialiased over a pixel and a half.
    float halfWidth = 0.5 * ${RING_WIDTH_PX.toFixed(1)} * uPixelRatio;
    float ring = 1.0 - smoothstep(halfWidth - 0.75, halfWidth + 0.75, abs(px - vRingPx));
    vec3 ringColor = mix(vec3(1.0), uColor, ${RING_TINT});
    // Additive blending adds the colour as it is (alpha 1).
    gl_FragColor = vec4((uColor * rest * (1.0 - vSel) + ringColor * ring * vSel) * uOpacity, 1.0);
  }
`;

export function createHaloMaterial({
  color = 0x4dd0ff,
  opacity = 0.9,
  globeRadius = 100.0,
  cellScale = 1.0,
} = {}) {
  return new THREE.ShaderMaterial({
    uniforms: {
      uColor: { value: new THREE.Color(color) },
      uOpacity: { value: opacity },
      uSelectedCellId: { value: 0.0 },
      uPrevSelectedCellId: { value: 0.0 },
      uHoveredCellId: { value: 0.0 },
      uPressTime: { value: -100.0 },
      uUnpressTime: { value: -100.0 },
      uGlobalFade: { value: 1.0 },
      uTime: { value: 0.0 },
      uGlobeRadius: { value: globeRadius },
      uSlotData: { value: null },
      uSlotTexWidth: { value: 1 },
      uSurfaceRadius: { value: globeRadius },
      uPointSize: { value: HALO_POINT_SIZE * cellScale },
      uHaloLift: { value: 2 * DOME_HEIGHT * cellScale },
      uViewportH: { value: 1000 },
      uPixelRatio: { value: 1 },
    },
    vertexShader: HALO_VERTEX_SHADER,
    fragmentShader: HALO_FRAGMENT_SHADER,
    transparent: true,
    depthWrite: false,
    side: THREE.FrontSide,
    blending: THREE.AdditiveBlending,
  });
}
